<script>
  import { Button, Icon } from "./ui/index.js";

  let { isOpen = false, onClose = () => {} } = $props();

  let email = $state("");
  let problem = $state("");
  let screenshot = $state(null);
  let screenshotPreview = $state(null);
  let isSubmitting = $state(false);
  let submitError = $state(null);
  let submitSuccess = $state(false);

  // Handle screenshot file selection
  function handleScreenshotChange(event) {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        submitError = "Please select an image file";
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        submitError = "Image must be less than 5MB";
        return;
      }
      screenshot = file;
      submitError = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        screenshotPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  // Remove screenshot
  function removeScreenshot() {
    screenshot = null;
    screenshotPreview = null;
    const input = document.getElementById("screenshot-input");
    if (input) input.value = "";
  }

  // Submit support request
  async function handleSubmit(event) {
    event.preventDefault();
    
    if (isSubmitting) return;

    // Validate
    if (!email || !problem) {
      submitError = "Please fill in all required fields";
      return;
    }

    isSubmitting = true;
    submitError = null;

    try {
      // Build form data
      const formData = new FormData();
      formData.append("email", email);
      formData.append("problem", problem);
      if (screenshot) {
        formData.append("screenshot", screenshot);
      }

      // Send request
      const response = await fetch("/api/support", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to submit support request");
      }

      // Success
      submitSuccess = true;
      setTimeout(() => {
        onClose();
        // Reset form after closing
        setTimeout(() => {
          email = "";
          problem = "";
          screenshot = null;
          screenshotPreview = null;
          submitSuccess = false;
        }, 300);
      }, 2000);
    } catch (err) {
      submitError = err.message;
    } finally {
      isSubmitting = false;
    }
  }

  // Close on escape key
  function handleKeydown(event) {
    if (event.key === "Escape" && isOpen) {
      onClose();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#if isOpen}  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-overlay" onclick={onClose}>
    <div class="modal-content" onclick={(e) => e.stopPropagation()}>      <div class="modal-header">
        <h2>
          <Icon name="alertCircle" size={24} />
          Report an Issue
        </h2>
        <button class="close-button" onclick={onClose} aria-label="Close">
          <Icon name="x" size={24} />
        </button>
      </div>

      {#if submitSuccess}
        <div class="success-message">
          <Icon name="checkCircle" size={48} />
          <h3>Issue Reported!</h3>
          <p>We'll review your report and get back to you soon.</p>
        </div>
      {:else}
        <form class="modal-body" onsubmit={handleSubmit}>
          <p class="modal-description">
            Experiencing a problem? Describe the issue below and we'll look into it.
          </p>

          <div class="form-group">
            <label for="support-email">
              Your Email <span class="required">*</span>
            </label>
            <input
              id="support-email"
              type="email"
              bind:value={email}
              placeholder="your.email@example.com"
              required
            />
          </div>          <div class="form-group">
            <label for="support-problem">
              Describe the issue <span class="required">*</span>
            </label>
            <textarea
              id="support-problem"
              bind:value={problem}
              placeholder="Please describe what happened and what you expected to happen..."
              rows="5"
              required
            ></textarea>
          </div>

          <div class="form-group">
            <label for="screenshot-input">
              Screenshot (optional)
            </label>
            {#if screenshotPreview}
              <div class="screenshot-preview">
                <img src={screenshotPreview} alt="Screenshot preview" />                <button
                  type="button"
                  class="remove-screenshot"
                  onclick={removeScreenshot}
                >
                  <Icon name="x" size={16} />
                  Remove
                </button>
              </div>
            {:else}
              <div class="file-input-wrapper">                <input
                  id="screenshot-input"
                  type="file"
                  accept="image/*"
                  onchange={handleScreenshotChange}
                />
                <label for="screenshot-input" class="file-input-label">
                  <Icon name="image" size={20} />
                  Choose Image
                </label>
                <span class="file-input-hint">PNG, JPG, or GIF (max 5MB)</span>
              </div>
            {/if}
          </div>

          {#if submitError}
            <div class="error-message">
              <Icon name="alertCircle" size={20} />
              {submitError}
            </div>          {/if}          <div class="modal-footer">
            <Button type="button" variant="secondary" onclick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {#if isSubmitting}
                Submitting...
              {:else}
                Submit Report
              {/if}
            </Button>
          </div>
        </form>
      {/if}
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: var(--space-4);
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .modal-content {
    background: white;
    border-radius: var(--radius-xl);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 540px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-6);
    border-bottom: 1px solid var(--color-gray-200);
  }

  .modal-header h2 {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
    font-size: var(--text-xl);
    font-weight: var(--font-semibold);
    color: var(--color-gray-900);
  }

  .close-button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-gray-500);
    padding: var(--space-1);
    border-radius: var(--radius-md);
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .close-button:hover {
    background: var(--color-gray-100);
    color: var(--color-gray-900);
  }

  .modal-body {
    padding: var(--space-6);
  }

  .modal-description {
    margin: 0 0 var(--space-6);
    color: var(--color-gray-600);
    font-size: var(--text-sm);
  }

  .form-group {
    margin-bottom: var(--space-5);
  }

  .form-group label {
    display: block;
    margin-bottom: var(--space-2);
    font-weight: var(--font-medium);
    color: var(--color-gray-700);
    font-size: var(--text-sm);
  }

  .required {
    color: #ef4444;
  }

  .form-group input[type="email"],
  .form-group textarea {
    width: 100%;
    padding: var(--space-3);
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-md);
    font-size: var(--text-base);
    font-family: inherit;
    transition: all 0.2s;
    box-sizing: border-box;
  }

  .form-group input[type="email"]:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 100px;
  }

  .file-input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .file-input-wrapper input[type="file"] {
    display: none;
  }

  .file-input-label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    background: var(--color-gray-100);
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-md);
    cursor: pointer;
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-gray-700);
    transition: all 0.2s;
  }

  .file-input-label:hover {
    background: var(--color-gray-200);
    border-color: var(--color-gray-400);
  }

  .file-input-hint {
    font-size: var(--text-xs);
    color: var(--color-gray-500);
  }

  .screenshot-preview {
    position: relative;
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .screenshot-preview img {
    width: 100%;
    display: block;
    max-height: 200px;
    object-fit: contain;
    background: var(--color-gray-50);
  }

  .remove-screenshot {
    position: absolute;
    top: var(--space-2);
    right: var(--space-2);
    display: flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-1) var(--space-2);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    cursor: pointer;
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    transition: all 0.2s;
  }

  .remove-screenshot:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  .error-message {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3);
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--radius-md);
    color: #b91c1c;
    font-size: var(--text-sm);
    margin-bottom: var(--space-4);
  }

  .success-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-10);
    text-align: center;
    color: #16a34a;
  }

  .success-message h3 {
    margin: var(--space-4) 0 var(--space-2);
    color: var(--color-gray-900);
    font-size: var(--text-xl);
  }

  .success-message p {
    margin: 0;
    color: var(--color-gray-600);
  }

  .modal-footer {
    display: flex;
    gap: var(--space-3);
    justify-content: flex-end;
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-gray-200);
  }
</style>
